import React, { useEffect, useMemo, useState } from 'react';
import axiosInstance from '../api/axiosInstance';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import './ResetPassword.css';

const EyeIcon = ({ on }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    {on ? (
      <>
        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" stroke="currentColor" strokeWidth="1.8" />
        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
      </>
    ) : (
      <>
        <path d="M3 3l18 18" stroke="currentColor" strokeWidth="1.8" />
        <path d="M9.9 5.1A10.9 10.9 0 0 1 12 5c6.5 0 10 7 10 7a18.1 18.1 0 0 1-4.1 4.94M6.1 7.94A18.2 18.2 0 0 0 2 12s3.5 7 10 7c1.1 0 2.17-.17 3.16-.47" stroke="currentColor" strokeWidth="1.8" fill="none"/>
        <path d="M9 9a3 3 0 1 0 4.24 4.24" stroke="currentColor" strokeWidth="1.8" fill="none"/>
      </>
    )}
  </svg>
);

const ResetPassword = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // token from ?token= or from hash (/#/reset-password?token=...)
  const token = useMemo(() => {
    const q = searchParams.get('token');
    if (q) return q;
    const hash = window.location.hash || '';
    const idx = hash.indexOf('?');
    if (idx >= 0) {
      const qs = new URLSearchParams(hash.slice(idx + 1));
      return qs.get('token');
    }
    return null;
  }, [searchParams]);

  useEffect(() => {
    if (!token) setError('Invalid or missing reset token. Please request a new reset link.');
  }, [token]);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    if (!token) return setError('Invalid or missing reset token. Please request a new reset link.');
    if (newPassword !== confirmPassword) return setError('Passwords do not match.');
    if (newPassword.length < 8) return setError('Password must be at least 8 characters long.');

    setIsLoading(true);
    try {
      await axiosInstance.post('/User/confirm-reset-password', {
        token,
        newPassword,
      });
    setMessage('Password reset successfully! Redirecting to login...');
      setTimeout(() => navigate('/login'), 1800);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password.');
    } finally {
      setIsLoading(false);
    }
  };

  const onEyeKeyDown = (e, toggle) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle(v => !v);
    }
  };

  return (
    <div className="rp-scope">
      <div className="rp-page">
        <div className="rp-card">
          <h1 className="rp-title">Set New Password</h1>

          {message && <div className="rp-alert rp-alert--success">{message}</div>}
          {error && <div className="rp-alert rp-alert--error">{error}</div>}

          <form className="rp-form" onSubmit={handleResetPassword} noValidate>
            <label htmlFor="newPassword" className="rp-label">New Password</label>
            <div className="rp-inputWrap">
              <input
                id="newPassword"
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 8 characters"
                className="rp-input"
                required
                disabled={isLoading}
                autoComplete="new-password"
              />
              <span
                className="rp-eye"
                role="button"
                aria-label={showNew ? 'Hide password' : 'Show password'}
                tabIndex={0}
                onClick={() => setShowNew(v => !v)}
                onKeyDown={(e) => onEyeKeyDown(e, setShowNew)}
              >
                <EyeIcon on={showNew} />
              </span>
            </div>

            <label htmlFor="confirmPassword" className="rp-label">Confirm New Password</label>
            <div className="rp-inputWrap">
              <input
                id="confirmPassword"
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter new password"
                className="rp-input"
                required
                disabled={isLoading}
                autoComplete="new-password"
              />
              <span
                className="rp-eye"
                role="button"
                aria-label={showConfirm ? 'Hide password' : 'Show password'}
                tabIndex={0}
                onClick={() => setShowConfirm(v => !v)}
                onKeyDown={(e) => onEyeKeyDown(e, setShowConfirm)}
              >
                <EyeIcon on={showConfirm} />
              </span>
            </div>

            <button type="submit" className="rp-btn" disabled={isLoading || !token}>
              {isLoading ? 'Resetting…' : 'Reset Password'}
            </button>

            <div className="rp-links">
              <Link to="/login" className="rp-link">Back to login</Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
